import { ChangeDetectionStrategy, Component, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  PickList,
  PickListMoveAllToSourceEvent,
  PickListMoveAllToTargetEvent,
  PickListMoveToSourceEvent,
  PickListMoveToTargetEvent
} from '@openng/optimus-ui/picklist';

@Component({
  selector: 'lib-pick-list',
  templateUrl: './pick-list.component.html',
  styleUrl: './pick-list.component.css',
  imports: [PickList, TranslocoDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PickListComponent)
    }
  ]
})
export class PickListComponent implements ControlValueAccessor {
  readonly source = input<any[]>([]);
  readonly scrollHeight = input<string>('500px');
  readonly dragdrop = input<boolean>(false);
  readonly showTargetFilter = input<boolean>(false);
  readonly showTargetControls = input<boolean>(false);
  readonly showSourceFilter = input<boolean>(false);
  readonly showSourceControls = input<boolean>(false);

  readonly target = signal<any[]>([]);

  private touched = false;
  disabled = false;

  // TODO: Save only values
  onMoveAllToSource(event: PickListMoveAllToSourceEvent) {
    this.changeValue([]);
  }

  handleMoveAllToTarget?(items: any[]): any[];

  onMoveAllToTarget(event: PickListMoveAllToTargetEvent) {
    let items = [...event.items];
    if (this.handleMoveAllToTarget) {
      items = this.handleMoveAllToTarget(items);
    }
    this.changeValue(items);
  }

  onMoveToSource(event: PickListMoveToSourceEvent) {
    let target = this.target();
    target = target.filter(
      (targetItem: any) => !event.items?.some(sourceItem => sourceItem === targetItem)
    );
    this.changeValue(target);
  }

  handleMoveToTarget?(items: any[]): any[];

  onMoveToTarget(event: PickListMoveToTargetEvent) {
    let items = [...event.items];
    if (this.handleMoveToTarget) {
      items = this.handleMoveToTarget(items);
    }
    const targetSet = new Set(this.target());
    items.forEach((item: any) => targetSet.add(item));
    this.changeValue(Array.from(targetSet));
  }

  changeValue(value?: any[]) {
    this.target.set(value || []);
    this.onChange(value);
    this.markAsTouched();
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  // Form methods

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: any[] | undefined): void {
    this.changeValue(
      this.source()?.filter((sourceItem: any) =>
        value?.some(targetItem => sourceItem['value'] === targetItem)
      ) || []
    );
  }

  private onChange = (value: any[] | undefined) => {};

  private onTouched = () => {};
}
